// selectPersonalActivityNodes.js

import { isNodeMediaTypeKey } from 'node/highlight/NodeMediaHighlight';
import { timestampToMillis } from 'utilities/DateTime';

export const PERSONAL_ACTIVITY_NODE_LIMIT = 32;

/**
 * Recency timestamp for a node.
 *
 * @param {object|null|undefined} node
 * @returns {number}
 */
function nodeRecencyMs(node) {
  return timestampToMillis(node?.dateModified)
    || timestampToMillis(node?.dateCreated)
    || 0;
}

/**
 * Prefer projects and tasks when capping the personal node set.
 *
 * @param {object|null|undefined} node
 * @returns {number}
 */
function typeRank(node) {
  const type = String(node?.type || '').trim().toUpperCase();
  if (type === 'PROJECT') return 2;
  if (type === 'TASK') return 1;
  return 0;
}

/**
 * Dedupes, drops media nodes, and caps the personal activity node set.
 *
 * @param {object[]} nodes
 * @param {{ limit?: number }} [options]
 * @returns {object[]}
 */
export function selectPersonalActivityNodes(nodes, {
  limit = PERSONAL_ACTIVITY_NODE_LIMIT,
  pinnedKeys = [],
} = {}) {
  const byKey = new Map();

  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const key = String(node?.key || '').trim();
    if (!key || !node) return;
    if (isNodeMediaTypeKey(node.type)) return;
    byKey.set(key, node);
  });

  const safeLimit = Number.isFinite(limit) && limit > 0
    ? limit
    : PERSONAL_ACTIVITY_NODE_LIMIT;

  const pinnedSet = new Set(
    (Array.isArray(pinnedKeys) ? pinnedKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  );

  const ranked = Array.from(byKey.values()).sort((left, right) => {
    const rank = typeRank(right) - typeRank(left);
    if (rank !== 0) return rank;
    return nodeRecencyMs(right) - nodeRecencyMs(left);
  });

  const pinned = [];
  const rest = [];
  ranked.forEach((node) => {
    if (pinnedSet.has(String(node.key).trim())) pinned.push(node);
    else rest.push(node);
  });

  const remaining = Math.max(0, safeLimit - pinned.length);
  return [...pinned, ...rest.slice(0, remaining)];
}
